import React, { useState, useRef } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Alert,
  Badge,
  Spinner,
} from "react-bootstrap";
import { AlertTriangle, Paperclip, X, CheckCircle, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createTicket } from "../../api/apiClient";

const CATEGORIES = [
  { value: 0, labelKey: "report.categoryErrors" },
  { value: 1, labelKey: "report.categoryInquiries" },
  { value: 2, labelKey: "report.categorySupportRequest" },
  { value: 3, labelKey: "report.categoryOther" },
];

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 10;

const ReportProblem = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    category: "",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [validated, setValidated] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFileError("");
    const selected = Array.from(e.target.files);
    const remaining = MAX_FILES - files.length;

    if (selected.length > remaining) {
      setFileError(t("report.fileMaxCount", { count: MAX_FILES }));
      e.target.value = "";
      return;
    }

    const oversized = selected.find(
      (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024
    );
    if (oversized) {
      setFileError(t("report.fileMaxSize", { size: MAX_FILE_SIZE_MB }));
      e.target.value = "";
      return;
    }

    setFiles((prev) => [
      ...prev,
      ...selected.map((f) => ({
        file: f,
        id: `${f.name}-${Date.now()}-${Math.random()}`,
        preview: f.type.startsWith("image/")
          ? URL.createObjectURL(f)
          : null,
      })),
    ]);

    e.target.value = "";
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Validar y parsear categoría
      const categoryValue = parseInt(formData.category, 10);

      // Validar que la categoría sea válida (0-3)
      if (isNaN(categoryValue) || categoryValue < 0 || categoryValue > 3) {
        setError("Por favor selecciona una categoría válida");
        setLoading(false);
        return;
      }

      // Obtener clientId y userId de sessionStorage
      const clientId = sessionStorage.getItem("clientId");
      const idUser = sessionStorage.getItem("idUser");

      // Preparar datos del ticket
      const ticketData = {
        title: formData.title,
        category: categoryValue,
        description: formData.description,
        status: 0, // Nuevo
        priority: 1, // Media
        clientId: clientId ? parseInt(clientId, 10) : null,
        userId: idUser ? parseInt(idUser, 10) : null,
      };

      // Preparar archivos
      const fileList = files.map((f) => f.file);

      // Enviar al API
      const response = await createTicket(ticketData, fileList);

      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.message || "Error al crear el ticket");
      }
    } catch (ex) {
      setError("Error inesperado al crear el ticket");
      console.error("Error creating ticket:", ex);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFormData({ title: "", category: "", description: "" });
    setFiles([]);
    setValidated(false);
    setSubmitted(false);
    setFileError("");
    setError("");
    setLoading(false);
  };

  if (submitted) {
    return (
      <Container className="py-5 d-flex justify-content-center">
        <Card
          style={{
            maxWidth: "480px",
            width: "100%",
            borderRadius: "20px",
            border: "none",
            background: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Card.Body className="p-5 text-center text-white">
            <CheckCircle size={64} className="mb-3" color="#10b981" />
            <h4 className="fw-bold mb-2">{t("report.successTitle")}</h4>
            <p className="mb-4" style={{ opacity: 0.85 }}>
              {t("report.successMessage")}
            </p>
            <Button
              variant="light"
              onClick={handleReset}
              style={{ borderRadius: "10px", fontWeight: 600 }}
            >
              {t("report.reportAnother")}
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: "640px" }}>
      <div className="d-flex align-items-center gap-2 mb-4">
        <AlertTriangle size={24} className="text-white" />
        <h4 className="text-white mb-0 fw-bold">{t("report.pageTitle")}</h4>
      </div>

      <Card
        style={{
          borderRadius: "20px",
          border: "none",
          background: "rgba(255,255,255,0.15)",
          backdropFilter: "blur(10px)",
        }}
      >
        <Card.Body className="p-4">
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            {/* Título */}
            <Form.Group className="mb-3">
              <Form.Label className="text-white fw-medium">
                {t("report.titleLabel")}
              </Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder={t("report.titlePlaceholder")}
                required
                minLength={5}
                maxLength={100}
                style={{
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                }}
              />
              <Form.Control.Feedback type="invalid">
                {t("report.titleRequired")}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Categoría */}
            <Form.Group className="mb-3">
              <Form.Label className="text-white fw-medium">
                {t("report.categoryLabel")}
              </Form.Label>
              <Form.Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                style={{
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                }}
              >
                <option value="">{t("report.categoryPlaceholder")}</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {t(cat.labelKey)}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {t("report.categoryRequired")}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Descripción */}
            <Form.Group className="mb-4">
              <Form.Label className="text-white fw-medium">
                {t("report.descriptionLabel")}
              </Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder={t("report.descriptionPlaceholder")}
                required
                minLength={10}
                rows={5}
                style={{
                  borderRadius: "10px",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                  resize: "none",
                }}
              />
              <Form.Control.Feedback type="invalid">
                {t("report.descriptionRequired")}
              </Form.Control.Feedback>
              <div
                className="text-end mt-1"
                style={{ fontSize: "0.78rem", opacity: 0.7, color: "white" }}
              >
                {formData.description.length} / 1000
              </div>
            </Form.Group>

            {/* Archivos adjuntos */}
            <Form.Group className="mb-4">
              <Form.Label className="text-white fw-medium">
                {t("report.filesLabel")}
                <span
                  className="ms-2"
                  style={{ fontSize: "0.8rem", opacity: 0.7 }}
                >
                  {t("report.filesHint", { count: MAX_FILES, size: MAX_FILE_SIZE_MB })}
                </span>
              </Form.Label>

              {/* Zona de carga */}
              <div
                onClick={() => files.length < MAX_FILES && fileInputRef.current?.click()}
                style={{
                  border: "2px dashed rgba(255,255,255,0.5)",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  textAlign: "center",
                  cursor: files.length < MAX_FILES ? "pointer" : "not-allowed",
                  opacity: files.length >= MAX_FILES ? 0.5 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                <Paperclip size={24} className="text-white mb-2" />
                <p className="text-white mb-0" style={{ fontSize: "0.9rem" }}>
                  {t("report.filesDropzone")}
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {fileError && (
                <Alert variant="danger" className="mt-2 py-2 mb-0" style={{ fontSize: "0.85rem" }}>
                  {fileError}
                </Alert>
              )}

              {/* Lista de archivos */}
              {files.length > 0 && (
                <div className="mt-3 d-flex flex-wrap gap-2">
                  {files.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        borderRadius: "8px",
                        padding: "6px 10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        maxWidth: "200px",
                      }}
                    >
                      {item.preview ? (
                        <img
                          src={item.preview}
                          alt=""
                          style={{
                            width: "32px",
                            height: "32px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                        />
                      ) : (
                        <Paperclip size={16} className="text-white" />
                      )}
                      <span
                        className="text-white"
                        style={{
                          fontSize: "0.78rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {item.file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(item.id)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "0",
                          color: "rgba(255,255,255,0.8)",
                          display: "flex",
                        }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {files.length > 0 && (
                <div className="mt-2">
                  <Badge
                    bg="light"
                    text="dark"
                    style={{ fontSize: "0.75rem" }}
                  >
                    {files.length}/{MAX_FILES} {t("report.filesAttached")}
                  </Badge>
                </div>
              )}
            </Form.Group>

            {/* Error message */}
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {/* Botones */}
            <div className="d-flex gap-3 justify-content-end">
              <Button
                type="button"
                variant="outline-light"
                onClick={handleReset}
                style={{ borderRadius: "10px" }}
                disabled={loading}
              >
                {t("report.cancelButton")}
              </Button>
              <Button
                type="submit"
                style={{
                  borderRadius: "10px",
                  background: "#10b981",
                  border: "none",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    {t("report.submittingButton") || "Enviando..."}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {t("report.submitButton")}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ReportProblem;
